import React, { useState, useEffect } from 'react';
import './AdminStyles.css';

interface Account {
  id: number;
  // WhatsApp
  phone?: string;
  phone_id?: string;
  // Qualidade do número na Meta (GREEN/YELLOW/RED; NA/UNKNOWN/null = sem avaliação).
  quality_rating?: string | null;
  // Permite/bloqueia disparos deste número; null/undefined (legado) conta como permitido.
  allow_shots?: boolean;
  // Instagram
  page_id?: string;
  instagram_account_id?: string;
  is_active: boolean;
  createdAt?: string;
  created_at?: string;
}

const QUALITY_LABELS: Record<string, { label: string; className: string }> = {
  GREEN: { label: 'Alta', className: 'high' },
  YELLOW: { label: 'Média', className: 'medium' },
  RED: { label: 'Baixa', className: 'low' },
};

interface AccountsListProps {
  type: 'wa' | 'ig';
  accounts: Account[];
  loading: boolean;
  onEdit: (account: Account) => void;
  onDelete: (id: number) => void;
  onToggle: (id: number) => void;
  // Só para WA: permite/bloqueia disparos do número.
  onToggleShots?: (id: number) => void;
}

export const AccountsList: React.FC<AccountsListProps> = ({
  type,
  accounts,
  loading,
  onEdit,
  onDelete,
  onToggle,
  onToggleShots
}) => {
  const getIdentifier = (account: Account) => {
    return type === 'wa' ? account.phone : account.page_id;
  };

  const getTitle = (account: Account) => {
    if (type === 'wa') {
      return account.phone_id ? `phone_id: ${account.phone_id}` : `WA: ${account.phone}`;
    }
    return account.instagram_account_id || `IG: ${account.page_id}`;
  };

  const getCreatedAt = (account: Account) => {
    const value = account.createdAt ?? account.created_at;
    return value ? new Date(value).toLocaleDateString('pt-BR') : '—';
  };

  const renderQuality = (account: Account) => {
    const quality = account.quality_rating ? QUALITY_LABELS[account.quality_rating] : undefined;
    if (!quality) {
      return <span className="quality-badge unknown" title="Sem avaliação da Meta">—</span>;
    }
    return <span className={`quality-badge ${quality.className}`}>{quality.label}</span>;
  };

  if (loading) {
    return <div className="accounts-loading">Carregando contas...</div>;
  }

  if (accounts.length === 0) {
    return (
      <div className="accounts-empty">
        <p>Nenhuma conta {type === 'wa' ? 'WhatsApp' : 'Instagram'} configurada</p>
      </div>
    );
  }

  return (
    <div className="accounts-table-container">
      <table className="accounts-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{type === 'wa' ? 'Phone ID' : 'Conta Instagram'}</th>
            <th>{type === 'wa' ? 'Telefone' : 'ID Página'}</th>
            {type === 'wa' && <th>Qualidade</th>}
            {type === 'wa' && <th>Disparos</th>}
            <th>Status</th>
            <th>Criado</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map(account => (
            <tr key={account.id} className={!account.is_active ? 'account-inactive' : ''}>
              <td>{account.id}</td>
              <td className="account-title">{getTitle(account)}</td>
              <td className="account-identifier">{getIdentifier(account)}</td>
              {type === 'wa' && <td>{renderQuality(account)}</td>}
              {type === 'wa' && (
                <td>
                  <button
                    className={`status-badge ${(account.allow_shots ?? true) ? 'active' : 'inactive'}`}
                    onClick={() => onToggleShots?.(account.id)}
                    title="Permitir/bloquear disparos deste número"
                  >
                    {(account.allow_shots ?? true) ? 'Permitido' : 'Bloqueado'}
                  </button>
                </td>
              )}
              <td>
                <button
                  className={`status-badge ${account.is_active ? 'active' : 'inactive'}`}
                  onClick={() => onToggle(account.id)}
                >
                  {account.is_active ? 'Ativo' : 'Inativo'}
                </button>
              </td>
              <td className="account-date">
                {getCreatedAt(account)}
              </td>
              <td className="account-actions">
                <button
                  className="btn-edit"
                  onClick={() => onEdit(account)}
                  title="Editar"
                >
                  ✏️
                </button>
                <button
                  className="btn-delete"
                  onClick={() => {
                    if (window.confirm(`Deletar conta ${getTitle(account)}?`)) {
                      onDelete(account.id);
                    }
                  }}
                  title="Deletar"
                >
                  🗑️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
